// src/pages/ToolkitPage.jsx - Profound Philosophy
// The Philosopher's Toolkit: eighteen interactive thinking tools, one per card,
// organised by the module whose ideas they exercise. Structure mirrors the
// toolkit page pattern shared across the DDD educational apps (lazy-loaded
// tool components, usage tracking through AppContext).
import React, { lazy, Suspense } from 'react';
import { ToolGate } from '../components/premium/PremiumGate';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageCircle,
  Search,
  Hammer,
  Pickaxe,
  ShieldCheck,
  Cpu,
  Gauge,
  Scale,
  GitBranch,
  Shuffle,
  Dices,
  Mountain,
  ClipboardCheck,
  Ship,
  Compass,
  Palette,
  EyeOff,
  ChevronLeft,
  Clock,
  Award,
  BookOpen,
  ArrowRight,
  Target,
  Brain,
  Zap,
  Lightbulb
} from 'lucide-react';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { useApp } from '../context/AppContext';

// Lazy load the philosophy tools for performance
const SocraticDialogueSimulator = lazy(() => import('../components/tools/SocraticDialogueSimulator'));
const ThreeQuestionFilter = lazy(() => import('../components/tools/ThreeQuestionFilter'));
const FallacyDetective = lazy(() => import('../components/tools/FallacyDetective'));
const ArgumentBuilder = lazy(() => import('../components/tools/ArgumentBuilder'));
const AssumptionExcavator = lazy(() => import('../components/tools/AssumptionExcavator'));
const CREDIBLEEvaluator = lazy(() => import('../components/tools/CREDIBLEEvaluator'));
const EpistemicConfidenceCalibrator = lazy(() => import('../components/tools/EpistemicConfidenceCalibrator'));
const EthicalFrameworkAnalyzer = lazy(() => import('../components/tools/EthicalFrameworkAnalyzer'));
const PersonalEthicsAuditor = lazy(() => import('../components/tools/PersonalEthicsAuditor'));
const PersonalIdentityExplorer = lazy(() => import('../components/tools/PersonalIdentityExplorer'));
const FreeWillTracker = lazy(() => import('../components/tools/FreeWillTracker'));
const ChineseRoomInteractive = lazy(() => import('../components/tools/ChineseRoomInteractive'));
const QualiaJournal = lazy(() => import('../components/tools/QualiaJournal'));
const PoliticalPhilosophyMapper = lazy(() => import('../components/tools/PoliticalPhilosophyMapper'));
const VeilOfIgnoranceAnalyzer = lazy(() => import('../components/tools/VeilOfIgnoranceAnalyzer'));
const ExistentialChoiceAnalyzer = lazy(() => import('../components/tools/ExistentialChoiceAnalyzer'));
const OriginalPositionGame = lazy(() => import('../components/tools/originalPosition/OriginalPositionGame'));
// The featured banner's living city — same lazy chunk family as the game.
const PromoVignette = lazy(() => import('../components/tools/originalPosition/PromoVignette'));
const MeaningCreationWorkshop = lazy(() => import('../components/tools/MeaningCreationWorkshop'));

const ToolkitPage = () => {
  // The active tool comes from the URL so tools are deep-linkable
  // (/toolkit/:toolId) — module pages link straight to their paired tools.
  const { toolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Where to offer a way back to. First choice: the module whose "Practise
  // This Now" card sent the reader here. Otherwise the module they were last
  // reading, within the same sitting — so reaching a tool from the Toolkit
  // nav still costs nobody their place.
  const recentModule = (() => {
    if (location.state?.fromModuleId) {
      return { id: location.state.fromModuleId, title: location.state.fromModuleTitle };
    }
    try {
      const last = JSON.parse(localStorage.getItem('profoundPhilosophy_lastModule'));
      const TWELVE_HOURS = 12 * 60 * 60 * 1000;
      if (last?.id && Date.now() - (last.at || 0) < TWELVE_HOURS) return last;
    } catch { /* nothing remembered */ }
    return null;
  })();
  const fromModuleId = recentModule?.id;
  const fromModuleTitle = recentModule?.title;
  const { toolsUsage = {}, updateToolUsage } = useApp();

  const tools = [
    {
      id: 'three-question-filter',
      title: 'The Three-Question Filter',
      subtitle: 'Sixty Seconds of Philosophy, Anywhere',
      description: 'Mid-conversation and there\'s a philosophical angle you can\'t quite drill into? Flick through the three checks from Module 1: is it factual or philosophical, what assumptions does it stand on, and what follows if it\'s true.',
      icon: Target,
      color: 'from-indigo-500 to-violet-500',
      category: 'What is Philosophy',
      estimatedTime: '1-2 mins',
      component: ThreeQuestionFilter,
      module: 1,
      priority: 1,
      features: [
        'The three checks, one screen each',
        'A quick test and a worked example per check',
        'Built to be used mid-conversation',
        'Hands off to your journal when done'
      ],
      whenToUse: 'When a claim lands in front of you (in a discussion, a feed, or your own head) and you want the philosophical read in under a minute'
    },
    {
      id: 'socratic-dialogue-simulator',
      title: 'Socratic Dialogue Journal',
      subtitle: 'Question Your Beliefs the Ancient Way',
      description: 'State one belief you hold, then answer five escalating rounds of Socratic questioning: clarifying it, probing its assumptions and evidence, testing its implications, and finally questioning the question itself.',
      icon: MessageCircle,
      color: 'from-indigo-500 to-violet-500',
      category: 'What is Philosophy',
      estimatedTime: '15-20 mins',
      component: SocraticDialogueSimulator,
      module: 1,
      priority: 1,
      features: [
        'Five staged rounds of Socratic questioning',
        'Guided prompts for each stage of the dig',
        'A saved library of completed dialogues',
        'Export any dialogue as a text file'
      ],
      whenToUse: 'When a belief feels obviously true and you want to find out what it is actually standing on'
    },
    {
      id: 'fallacy-detective',
      title: 'Fallacy Detective',
      subtitle: 'Train Your Reasoning Radar',
      description: 'A scored training game: read real-world style arguments from politics, advertising and everyday life, then identify which of twelve common logical fallacies each one commits.',
      icon: Search,
      color: 'from-blue-500 to-cyan-500',
      category: 'Logic & Reasoning',
      estimatedTime: '10-15 mins',
      component: FallacyDetective,
      module: 2,
      priority: 1,
      features: [
        'Reference library of twelve common fallacies',
        'Worked examples from politics, advertising and daily life',
        'Scored rounds with explanations for every answer',
        'Accuracy stats tracked across sessions'
      ],
      whenToUse: 'When you want persuasion attempts, including your own, to stop slipping past you unexamined'
    },
    {
      id: 'argument-builder',
      title: 'Argument Builder',
      subtitle: 'Construct Reasoning That Holds',
      description: 'Build an argument premise by premise and watch it get checked as you go: the tool flags conclusions that smuggle in new terms, lonely single premises, and vague quantifiers hiding in deductive clothing.',
      icon: Hammer,
      color: 'from-blue-500 to-cyan-500',
      category: 'Logic & Reasoning',
      estimatedTime: '10-15 mins',
      component: ArgumentBuilder,
      module: 2,
      priority: 2,
      features: [
        'Premise-by-premise construction with add/remove rows',
        'Deductive and inductive argument modes',
        'Automatic structural checks and warnings',
        'A saved library with per-argument export'
      ],
      whenToUse: 'Before a hard conversation, an important email, or any moment your case needs to actually hold together'
    },
    {
      id: 'assumption-excavator',
      title: 'Assumption Excavator',
      subtitle: 'Dig Beneath What You Believe',
      description: 'Take one belief and excavate it through five layers, from the surface statement down through immediate assumptions, value assumptions and worldview, to a final critical examination of what you found.',
      icon: Pickaxe,
      color: 'from-teal-500 to-emerald-500',
      category: 'Epistemology',
      estimatedTime: '20 mins',
      component: AssumptionExcavator,
      module: 3,
      priority: 1,
      features: [
        'Five excavation layers from surface to worldview',
        'Guided prompts at every depth',
        'Layer-by-layer progress tracking',
        'Export your excavation as a text file'
      ],
      whenToUse: 'When a disagreement keeps going in circles and you suspect the real dispute is buried somewhere deeper'
    },
    {
      id: 'credible-evaluator',
      title: 'CREDIBLE Framework Evaluator',
      subtitle: 'Navigate Truth in the Misinformation Age',
      description: 'Put any claim and its source through eight credibility checks (credentials, reputation, evidence, disclosure, independent verification, bias, logic and evidence quality) and get an overall verdict.',
      icon: ShieldCheck,
      color: 'from-teal-500 to-emerald-500',
      category: 'Epistemology',
      estimatedTime: '15-20 mins',
      component: CREDIBLEEvaluator,
      module: 3,
      priority: 2,
      features: [
        'Eight-point CREDIBLE checklist with probing questions',
        'Yes / partial / no scoring per criterion',
        'An overall credibility verdict for the claim',
        'Saved evaluations you can export'
      ],
      whenToUse: 'When a claim is spreading fast, sounds convincing, and you need a systematic way to judge it'
    },
    {
      id: 'epistemic-confidence-calibrator',
      title: 'Epistemic Confidence Calibrator',
      subtitle: 'Match Your Certainty to Your Evidence',
      description: 'Log beliefs with a confidence percentage and the evidence behind each one. The calibrator maps your certainty across nine confidence bands and shows where conviction is outrunning justification.',
      icon: Gauge,
      color: 'from-teal-500 to-emerald-500',
      category: 'Epistemology',
      estimatedTime: '5 mins per entry',
      component: EpistemicConfidenceCalibrator,
      module: 3,
      priority: 3,
      features: [
        'Confidence slider with nine named certainty bands',
        'Eight belief categories, each with its typical failure mode',
        'Overconfidence detection across your entries',
        'Calibration analysis you can export'
      ],
      whenToUse: 'As a recurring practice whenever you notice yourself saying "definitely" about anything'
    },
    {
      id: 'ethical-framework-analyzer',
      title: 'Ethical Framework Analyser',
      subtitle: 'Three Lenses on One Dilemma',
      description: 'Describe a real moral dilemma and reason through it three times (as a consequentialist weighing outcomes, a Kantian testing duties, and a virtue ethicist asking what character demands), then compare the verdicts.',
      icon: Scale,
      color: 'from-amber-500 to-orange-500',
      category: 'Ethics',
      estimatedTime: '20-25 mins',
      component: EthicalFrameworkAnalyzer,
      module: 4,
      priority: 1,
      features: [
        'Consequentialist, deontological and virtue ethics lenses',
        'Guided analysis questions for each framework',
        'Worked examples showing each lens in action',
        'Side-by-side comparison of all three verdicts'
      ],
      whenToUse: 'When you face a genuine dilemma and your gut answer needs to be checked against more than your gut'
    },
    {
      id: 'personal-ethics-auditor',
      title: 'Personal Ethics Auditor',
      subtitle: 'Where Your Values Meet Your Calendar',
      description: 'Audit the gap between what you value and how you actually live across six domains (relationships, work, community, consumption, character and self-care) with honest scoring and pointed prompts.',
      icon: ClipboardCheck,
      color: 'from-amber-500 to-orange-500',
      category: 'Ethics',
      estimatedTime: '20-25 mins',
      component: PersonalEthicsAuditor,
      module: 4,
      priority: 2,
      features: [
        'Six life domains with four probing prompts each',
        'Five-point alignment scoring per domain',
        'Strengths and gaps identified in your results',
        'Exportable audit to revisit later'
      ],
      whenToUse: 'When life feels vaguely misaligned and you want data instead of a vague feeling'
    },
    {
      id: 'personal-identity-explorer',
      title: 'Personal Identity Explorer',
      subtitle: 'What Makes You "You" Across Time?',
      description: 'Work through five classic identity puzzles (the Ship of Theseus, split brains, gradual brain replacement, the teleporter and total memory loss) and record where your intuitions land.',
      icon: Ship,
      color: 'from-purple-500 to-fuchsia-500',
      category: 'Metaphysics',
      estimatedTime: '20-25 mins',
      component: PersonalIdentityExplorer,
      module: 5,
      priority: 1,
      features: [
        'Five classic thought experiments, fully narrated',
        'Probing questions after every scenario',
        'Real-world tie-ins from dementia to mind uploading',
        'A synthesis of where your intuitions settled'
      ],
      whenToUse: 'When you want to test what actually connects the person you were to the person reading this'
    },
    {
      id: 'free-will-tracker',
      title: 'Free Will Experience Tracker',
      subtitle: 'How Free Do Your Choices Feel?',
      description: 'A running log of real decisions: rate each one for sense of agency, deliberation and predictability, record the constraints and the road not taken, then watch patterns emerge across your entries.',
      icon: Shuffle,
      color: 'from-purple-500 to-fuchsia-500',
      category: 'Metaphysics',
      estimatedTime: '5 mins per entry',
      component: FreeWillTracker,
      module: 5,
      priority: 2,
      features: [
        'Three sliders per decision: agency, deliberation, predictability',
        'Constraint and alternative tracking for every choice',
        'Running averages and pattern insights',
        'Export your full decision log'
      ],
      whenToUse: 'As an ongoing practice when the free will debate stops being abstract and starts being about your Tuesday'
    },
    {
      id: 'chinese-room-interactive',
      title: 'Chinese Room Interactive',
      subtitle: 'Can Machines Understand, or Only Simulate?',
      description: "Step inside Searle's famous thought experiment stage by stage (the room, the observer, the parallel to modern AI, and the classic replies), then commit to a position on machine understanding.",
      icon: Cpu,
      color: 'from-pink-500 to-rose-500',
      category: 'Philosophy of Mind',
      estimatedTime: '15 mins',
      component: ChineseRoomInteractive,
      module: 6,
      priority: 1,
      features: [
        'Four narrative stages of the thought experiment',
        'Reflection questions at every stage',
        'The major counter-arguments, fairly presented',
        'A position picker: agree, disagree, or genuinely torn'
      ],
      whenToUse: 'Right after a chatbot conversation that felt a little too much like talking to someone'
    },
    {
      id: 'qualia-journal',
      title: 'Qualia Investigation Journal',
      subtitle: 'The Felt Texture of Experience',
      description: 'A journal for the "what it\'s like" of consciousness: capture visual, auditory, tactile, taste, smell and emotional experiences, then try describing what stubbornly resists description.',
      icon: Palette,
      color: 'from-pink-500 to-rose-500',
      category: 'Philosophy of Mind',
      estimatedTime: '5-10 mins per entry',
      component: QualiaJournal,
      module: 6,
      priority: 2,
      features: [
        'Six experience types with concrete examples',
        'Phenomenological prompts that push past physical description',
        'An ineffability note for what words cannot reach',
        'A dated history of your investigations'
      ],
      whenToUse: 'When an experience is vivid and you want to study consciousness with the one instrument that has it'
    },
    {
      id: 'political-philosophy-mapper',
      title: 'Political Philosophy Mapper',
      subtitle: 'The Framework Under Your Politics',
      description: 'Choose a topic and answer five policy questions (or a mixed run of eight) by picking the reasoning you find most convincing. Five traditions sit behind the options, utilitarian, libertarian, Rawlsian, communitarian and egalitarian, and stay hidden until the end.',
      icon: Compass,
      color: 'from-sky-500 to-blue-600',
      category: 'Society & Justice',
      estimatedTime: '10-15 mins',
      component: PoliticalPhilosophyMapper,
      module: 7,
      priority: 1,
      features: [
        'Forty policy questions across eight topics, from tax to the environment',
        'Choose between framework-authored positions, not agree/disagree',
        'Alignment percentages across five traditions',
        'A strong, moderate or mixed reading of your result'
      ],
      whenToUse: 'When you can predict your own political reactions but cannot yet explain the principle behind them'
    },
    {
      id: 'veil-of-ignorance-analyzer',
      title: 'Veil of Ignorance Analyser',
      subtitle: "Fairness, Tested Rawls' Way",
      description: "Judge a policy twice: once from your actual position in society, once from behind the veil where you don't know your class, health, age or postcode. Then study what changes between the two verdicts.",
      icon: EyeOff,
      color: 'from-sky-500 to-blue-600',
      category: 'Society & Justice',
      estimatedTime: '15 mins',
      component: VeilOfIgnoranceAnalyzer,
      module: 7,
      priority: 2,
      features: [
        'Ten blindness factors to deliberately set aside',
        'Structured before-and-behind-the-veil comparison',
        'A fairness verdict grounded in the difference',
        'Saved analyses with export'
      ],
      whenToUse: 'When you hold a strong policy opinion and want to know how much of it is principle and how much is postcode'
    },
    {
      id: 'original-position',
      title: 'The Original Position',
      subtitle: 'Build the World, Then Be Born Into It',
      description: 'A justice game played against your own judgement: design a society with fourteen high-stakes levers, armed with exactly one true hint about who you\'ll be, then get born into your own rules as a random citizen, take fate\'s swing, and read the dual score. Parlour mode deals a whole table in.',
      icon: Dices,
      color: 'from-sky-500 to-blue-600',
      category: 'Society & Justice',
      estimatedTime: '10-15 mins',
      component: OriginalPositionGame,
      module: 7,
      priority: 3,
      features: [
        'A living city that reacts to every lever you pull',
        'One true, never-sufficient hint before you design',
        'A paced identity draw, a fate event, and a dual score',
        'Four named endings and a philosopher debrief',
        'Parlour mode: pass-and-play with private hints around a table'
      ],
      whenToUse: 'When you want to find out whether you\'d sign the society you say you believe in, with your own next life as the stake'
    },
    {
      id: 'existential-choice-analyzer',
      title: 'Existential Choice Analyser',
      subtitle: 'Authenticity or Bad Faith?',
      description: "Take one real choice you've made and test it against five markers of authenticity (awareness of freedom, owned responsibility, value alignment, faced anxiety and self-honesty) with Sartre looking over your shoulder.",
      icon: GitBranch,
      color: 'from-violet-500 to-purple-600',
      category: 'Existentialism & Meaning',
      estimatedTime: '15-20 mins',
      component: ExistentialChoiceAnalyzer,
      module: 8,
      priority: 1,
      features: [
        'Five authenticity dimensions with named bad-faith signs',
        'Guidance drawn from Sartre and Kierkegaard',
        'Reflection plus honest self-assessment per dimension',
        'A summary scorecard for the choice'
      ],
      whenToUse: 'When "I had no choice" is doing suspicious amounts of work in the story you tell about a decision'
    },
    {
      id: 'meaning-creation-workshop',
      title: 'Meaning Creation Workshop',
      subtitle: 'Build Meaning in an Absurd Universe',
      description: "A Camus-framed workshop: reflect on four sources of meaning (creative work, experiences, connection and causes), then turn the reflections into concrete meaning projects with commitments and first steps.",
      icon: Mountain,
      color: 'from-violet-500 to-purple-600',
      category: 'Existentialism & Meaning',
      estimatedTime: '25-30 mins',
      component: MeaningCreationWorkshop,
      module: 8,
      priority: 2,
      features: [
        'Four meaning domains, each with a Camus provocation',
        'Guided reflection prompts per domain',
        'Meaning projects with commitments and first steps',
        'A revisitable workshop that exports as JSON'
      ],
      whenToUse: 'When the question "what is the point?" has stopped being rhetorical and deserves a working answer'
    }
  ];

  // The Original Position gets a featured banner above the grid rather than
  // a card within it — it is the toolkit's game, not one tool among many.
  const featuredTool = tools.find((t) => t.id === 'original-position');

  // Sort tools by module order, then priority within the module
  const sortedTools = [...tools]
    .filter((t) => t.id !== 'original-position')
    .sort((a, b) => {
      if (a.module === b.module) return a.priority - b.priority;
      return a.module - b.module;
    });

  const activeTool = toolId && tools.some(t => t.id === toolId) ? toolId : null;

  const openTool = (tool) => {
    navigate(`/toolkit/${tool.id}`);
    // Record the session so the dashboard and gamification see tool activity
    updateToolUsage?.(tool.id, {
      uses: (toolsUsage[tool.id]?.uses || 0) + 1,
      lastUsed: new Date().toISOString()
    });
  };

  const ToolCard = ({ tool }) => {
    const stats = toolsUsage[tool.id] || { uses: 0, lastUsed: null };

    return (
      <Card
        hover
        className="p-4 sm:p-6 h-full cursor-pointer"
        onClick={() => openTool(tool)}
      >
        <div className="space-y-3 sm:space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center bg-gradient-to-br ${tool.color} flex-shrink-0`}>
              <tool.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>

            <div className="text-right">
              <span className="text-xs text-slate-400">{tool.category}</span>
              {stats.uses > 0 && (
                <div className="text-xs text-green-400 mt-1">
                  Used {stats.uses}x
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{tool.title}</h3>
            <p className="text-xs sm:text-sm text-indigo-200 font-medium mb-2 sm:mb-3">{tool.subtitle}</p>
            <p className="text-slate-300 text-xs sm:text-sm mb-3 sm:mb-4">{tool.description}</p>

            {/* When to Use */}
            <div className="p-2 sm:p-3 bg-blue-500/10 rounded-lg mb-3 sm:mb-4">
              <p className="text-xs text-blue-300 font-medium mb-1">When to Use:</p>
              <p className="text-xs text-slate-300">{tool.whenToUse}</p>
            </div>

            {/* Features */}
            <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
              {tool.features.slice(0, 2).map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-slate-400">
                  <div className="w-1 h-1 bg-indigo-400 rounded-full" />
                  {feature}
                </div>
              ))}
              {tool.features.length > 2 && (
                <div className="text-xs text-slate-400">
                  +{tool.features.length - 2} more features
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-slate-700">
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Clock className="w-3 h-3" />
                {tool.estimatedTime}
              </span>

              <Button
                size="sm"
                variant="ghost"
                className="text-xs sm:text-sm"
                onClick={() => openTool(tool)}
                aria-label={`Open ${tool.title}`}
              >
                <span className="hidden sm:inline">Open Tool</span>
                <span className="sm:hidden">Open</span>
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  if (activeTool) {
    const tool = tools.find(t => t.id === activeTool);
    const ToolComponent = tool.component;

    // The Original Position is deliberately NOT gated here — it is always
    // openable, and its own modes carry the gate (accessModel.GAME_MODE_ACCESS).
    return (
      <div className="min-h-screen bg-gradient-to-br from-charcoal to-gunmetal pt-24 md:pt-32">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="max-w-6xl mx-auto">
            {/* Back buttons: when a module sent the reader here, offer the
                way back to it first, so practising a tool doesn't cost them
                their place in the module. */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-6 sm:mb-8">
              {fromModuleId && (
                <button
                  onClick={() => navigate(`/modules/${fromModuleId}`)}
                  className="flex items-center gap-2 text-bright-gold hover:text-white transition-colors font-medium"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to {fromModuleTitle || 'the module'}
                </button>
              )}
              <button
                onClick={() => navigate('/toolkit')}
                className="flex items-center gap-2 text-indigo-300 hover:text-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Toolkit
              </button>
            </div>

            {/* Tool Component */}
            <ToolGate toolId={activeTool} title={tool.title}>
            <Suspense fallback={
              <div className="flex items-center justify-center p-12" role="status">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
                  <p className="text-slate-400">Loading tool...</p>
                </div>
              </div>
            }>
              <ToolComponent />
            </Suspense>
            </ToolGate>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal to-gunmetal pt-24 md:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section: clearing after rain */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative text-center mb-8 sm:mb-12 rounded-3xl overflow-hidden py-10 px-4"
          >
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
              <img
                src="/images/cloud-toolkit.webp"
                alt=""
                className="w-full h-full object-cover opacity-60"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-gunmetal/55 via-gunmetal/30 to-gunmetal" />
            </div>
            <div className="relative">
              {/* Hero Icon */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-6 inline-flex items-center justify-center w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 mx-auto"
              >
                <Lightbulb className="w-12 h-12 md:w-16 md:h-16 text-white" />
              </motion.div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4 px-4">
                The Philosopher's
                <span className="block bg-gradient-to-r from-indigo-400 via-violet-400 to-purple-400 bg-clip-text text-transparent">
                  Toolkit
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-200 max-w-2xl mx-auto mb-4 sm:mb-6 px-4">
                18 interactive tools that turn philosophy from something you read into something you do. Question beliefs, build arguments, test intuitions, one tool at a time.
              </p>
              <p className="text-base sm:text-lg text-slate-300 max-w-3xl mx-auto px-4">
                Each tool exercises ideas taught in the modules (from Socrates and Aristotle to Rawls, Searle and Camus), so the thinking you practise here has 2,500 years of philosophy behind it.
              </p>
            </div>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-12"
          >
            <Card className="p-3 sm:p-4 text-center">
              <Award className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">
                {Object.values(toolsUsage).reduce((sum, stat) => sum + (stat.uses || 0), 0)}
              </div>
              <div className="text-xs sm:text-sm text-slate-400">Total Tool Uses</div>
            </Card>

            <Card className="p-3 sm:p-4 text-center">
              <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">
                {Object.keys(toolsUsage).length}
              </div>
              <div className="text-xs sm:text-sm text-slate-400">Tools Explored</div>
            </Card>

            <Card className="p-3 sm:p-4 text-center">
              <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">8</div>
              <div className="text-xs sm:text-sm text-slate-400">Philosophy Areas</div>
            </Card>

            <Card className="p-3 sm:p-4 text-center">
              <Target className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">18</div>
              <div className="text-xs sm:text-sm text-slate-400">Total Tools</div>
            </Card>
          </motion.div>

          {/* Featured: The Original Position, the toolkit's game */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-8 sm:mb-12 rounded-3xl border-2 border-bright-gold/50 bg-gradient-to-r from-sky-900/30 via-slate-900/60 to-violet-900/30 p-5 sm:p-8 overflow-hidden"
          >
            <div className="grid lg:grid-cols-2 gap-6 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-bright-gold mb-2">
                  Featured · The justice game
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">The Original Position</h2>
                <p className="text-indigo-200 font-medium mb-3">Build the world. Then be born into it.</p>
                <p className="text-sm text-slate-300 mb-4">
                  Design a society with fourteen high-stakes levers, armed with exactly one true hint
                  about who you’ll be, then get born into your own rules as a random citizen, take
                  fate’s swing, and find out which philosopher you played. A living city reacts to
                  every choice.
                </p>
                <div className="flex flex-wrap gap-2 mb-5 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-slate-900/60 border border-slate-600 text-slate-300">Solo · 10–15 mins</span>
                  <span className="px-2.5 py-1 rounded-full bg-slate-900/60 border border-slate-600 text-slate-300">Parlour · one phone, 2–8 players</span>
                  <span className="px-2.5 py-1 rounded-full bg-slate-900/60 border border-slate-600 text-slate-300">Linked table · phones + QR</span>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <Button size="large" onClick={() => featuredTool && openTool(featuredTool)}>
                    Play The Original Position
                    <ArrowRight className="w-5 h-5 ml-2 inline" />
                  </Button>
                  {(toolsUsage['original-position']?.uses || 0) > 0 && (
                    <span className="text-xs text-green-400">
                      Played {toolsUsage['original-position'].uses}x
                    </span>
                  )}
                </div>
              </div>
              <Suspense fallback={<div className="rounded-2xl border border-slate-700 bg-gunmetal min-h-[16rem]" />}>
                <PromoVignette />
              </Suspense>
            </div>
          </motion.div>

          {/* Tools Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12"
          >
            {sortedTools.map((tool, idx) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * idx }}
              >
                <ToolCard tool={tool} />
              </motion.div>
            ))}
          </motion.div>

          {/* Usage Tips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="p-4 sm:p-6 md:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 text-center">
                How to Use Your Toolkit
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Start with Reasoning</h3>
                  <p className="text-slate-300 text-xs sm:text-sm">
                    The Socratic Simulator, Fallacy Detective and Argument Builder sharpen the thinking skills every other tool relies on. They're the whetstone for the rest.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Follow Your Module</h3>
                  <p className="text-slate-300 text-xs sm:text-sm">
                    Each tool pairs with a module. Finished the ethics module? The Framework Analyser and Ethics Auditor turn what you learnt into something you can apply the same day.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Make It a Practice</h3>
                  <p className="text-slate-300 text-xs sm:text-sm">
                    The Confidence Calibrator, Free Will Tracker and Qualia Journal are built for repeat visits. A few minutes at a time, they turn philosophy into a habit rather than an event.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ToolkitPage;
